import {
  NotificationChannel,
  NotificationTemplate,
  NotificationTemplateContentHistory,
  RenderTemplatePreviewRequest,
  RenderTemplatePreviewResponse,
  NotificationRetryPolicy,
  NotificationRule,
  NotificationRuleRecipient,
  NotificationDeliveryLog,
  CreateNotificationChannel,
  UpdateNotificationChannel,
  CreateNotificationTemplate,
  UpdateNotificationTemplate,
  CreateNotificationRetryPolicy,
  CreateNotificationRule,
  UpdateNotificationRule,
  CreateNotificationRuleRecipient,
  TriggerNotificationRequest,
  SaveProgramNotificationConfigurationRequest,
  RuleSimulationRequest,
  RuleSimulationResult,
  NotificationRuleVersion,
  RuleVersionComparison,
  RuleVersionRollbackRequest
} from '../types/notificationConfiguration';
import { apiClient } from '@/modules/shared/services/apiClient';
import { ApiResponse } from '@/modules/shared/types';

const API_PATH = '/programs/notification-configuration';

/**
 * Notification Channel API
 */
export const notificationChannelApi = {
  getAll: async (): Promise<NotificationChannel[]> => {
    const response = await apiClient.get(`${API_PATH}/channels`, { params: { includeArchived: true } });
    return response.data.data;
  },

  getById: async (id: number): Promise<NotificationChannel> => {
    const response = await apiClient.get(`${API_PATH}/channels/${id}`);
    return response.data.data;
  },

  create: async (data: CreateNotificationChannel): Promise<NotificationChannel> => {
    const response = await apiClient.post(`${API_PATH}/channels`, data);
    return response.data.data;
  },

  update: async (id: number, data: UpdateNotificationChannel): Promise<NotificationChannel> => {
    const response = await apiClient.put(`${API_PATH}/channels/${id}`, data);
    return response.data.data;
  },

  toggleActive: async (id: number): Promise<NotificationChannel> => {
    const channel = await notificationChannelApi.getById(id);
    return notificationChannelApi.update(id, {
      channelName: channel.channelName,
      isActive: !channel.isActive,
      configurationJson: channel.configurationJson
    });
  },

  archive: async (id: string): Promise<NotificationChannel> => {
    const response = await apiClient.post(`${API_PATH}/channels/${id}/archive`);
    return response.data.data;
  },

  delete: async (id: string): Promise<boolean> => {
    const response = await apiClient.delete(`${API_PATH}/channels/${id}`);
    return response.data.data;
  },

  restore: async (id: string): Promise<NotificationChannel> => {
    const response = await apiClient.post(`${API_PATH}/channels/${id}/restore`);
    return response.data.data;
  }
};

/**
 * Notification Template API
 */
export const notificationTemplateApi = {
  getAll: async (): Promise<NotificationTemplate[]> => {
    const response = await apiClient.get(`${API_PATH}/templates`, { params: { includeArchived: true } });
    return response.data.data;
  },

  getById: async (id: number): Promise<NotificationTemplate> => {
    const response = await apiClient.get(`${API_PATH}/templates/${id}`);
    return response.data.data;
  },

  create: async (data: CreateNotificationTemplate): Promise<NotificationTemplate> => {
    const response = await apiClient.post(`${API_PATH}/templates`, data);
    return response.data.data;
  },

  update: async (id: number, data: UpdateNotificationTemplate): Promise<NotificationTemplate> => {
    const response = await apiClient.put(`${API_PATH}/templates/${id}`, data);
    return response.data.data;
  },

  archive: async (id: string): Promise<NotificationTemplate> => {
    const response = await apiClient.post(`${API_PATH}/templates/${id}/archive`);
    return response.data.data;
  },

  delete: async (id: string): Promise<boolean> => {
    const response = await apiClient.delete(`${API_PATH}/templates/${id}`);
    return response.data.data;
  },

  restore: async (id: string): Promise<NotificationTemplate> => {
    const response = await apiClient.post(`${API_PATH}/templates/${id}/restore`);
    return response.data.data;
  },

  getHistory: async (id: string): Promise<NotificationTemplateContentHistory[]> => {
    const response = await apiClient.get(`${API_PATH}/templates/${id}/history`);
    return response.data.data;
  },

  renderPreview: async (id: string, data: RenderTemplatePreviewRequest): Promise<RenderTemplatePreviewResponse> => {
    const response = await apiClient.post(`${API_PATH}/templates/${id}/render-preview`, data);
    return response.data.data;
  }
};

/**
 * Notification Retry Policy API
 */
export const notificationRetryPolicyApi = {
  getAll: async (): Promise<NotificationRetryPolicy[]> => {
    const response = await apiClient.get(`${API_PATH}/retry-policies`);
    return response.data.data;
  },

  create: async (data: CreateNotificationRetryPolicy): Promise<NotificationRetryPolicy> => {
    const response = await apiClient.post(`${API_PATH}/retry-policies`, data);
    return response.data.data;
  }
};

/**
 * Notification Rule API
 */
export const notificationRuleApi = {
  getAll: async (): Promise<NotificationRule[]> => {
    const response = await apiClient.get(`${API_PATH}/rules`);
    return response.data.data;
  },

  getById: async (id: number): Promise<NotificationRule> => {
    const response = await apiClient.get(`${API_PATH}/rules/${id}`);
    return response.data.data;
  },

  create: async (data: CreateNotificationRule): Promise<NotificationRule> => {
    const response = await apiClient.post(`${API_PATH}/rules`, data);
    return response.data.data;
  },

  update: async (id: number, data: UpdateNotificationRule): Promise<NotificationRule> => {
    const response = await apiClient.put(`${API_PATH}/rules/${id}`, data);
    return response.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`${API_PATH}/rules/${id}`);
  },

  toggleActive: async (id: number): Promise<NotificationRule> => {
    const rule = await notificationRuleApi.getById(id);
    return notificationRuleApi.update(id, {
      ruleName: rule.ruleName,
      eventType: rule.eventType,
      notificationChannelId: rule.notificationChannelId,
      notificationTemplateId: rule.notificationTemplateId,
      isActive: !rule.isActive,
      priority: rule.priority,
      conditionsJson: rule.condition,
      notificationRetryPolicyId: rule.notificationRetryPolicyId,
      escalationTemplateId: rule.escalationTemplateId,
      escalationDelayMinutes: rule.escalationDelayMinutes,
      changeSummary: `Toggled rule ${rule.ruleName} active state`
    });
  },

  simulate: async (programId: string, data: RuleSimulationRequest): Promise<ApiResponse<RuleSimulationResult>> => {
    const response = await apiClient.post(`${API_PATH}/${programId}/rules/simulate`, data);
    return response.data;
  },

  getVersions: async (ruleId: string): Promise<NotificationRuleVersion[]> => {
    const response = await apiClient.get(`${API_PATH}/rules/${ruleId}/versions`);
    return response.data.data;
  },

  compareVersions: async (ruleId: string, sourceVersionId: string, targetVersionId: string): Promise<RuleVersionComparison> => {
    const response = await apiClient.get(`${API_PATH}/rules/${ruleId}/versions/compare`, {
      params: { sourceVersionId, targetVersionId }
    });
    return response.data.data;
  },

  rollbackVersion: async (ruleId: string, versionId: string, data: RuleVersionRollbackRequest): Promise<NotificationRuleVersion> => {
    const response = await apiClient.post(`${API_PATH}/rules/${ruleId}/versions/${versionId}/rollback`, data);
    return response.data.data;
  }
};

/**
 * Notification Rule Recipient API
 */
export const notificationRuleRecipientApi = {
  create: async (ruleId: string, data: CreateNotificationRuleRecipient): Promise<NotificationRuleRecipient> => {
    const response = await apiClient.post(`${API_PATH}/rules/${ruleId}/recipients`, data);
    return response.data.data;
  },

  delete: async (ruleId: number, recipientId: number): Promise<void> => {
    await apiClient.delete(`${API_PATH}/rules/${ruleId}/recipients/${recipientId}`);
  }
};

/**
 * Notification Delivery Log API
 */
export const notificationDeliveryLogApi = {
  getAll: async (params?: {
    ruleId?: number;
    channelId?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<NotificationDeliveryLog[]> => {
    const queryParams: Record<string, string> = {};
    if (params?.ruleId) queryParams.ruleId = params.ruleId.toString();
    if (params?.channelId) queryParams.channelId = params.channelId.toString();
    if (params?.status) queryParams.status = params.status;
    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;

    const response = await apiClient.get(`${API_PATH}/delivery-logs`, { params: queryParams });
    return response.data.data;
  },

  getById: async (id: number): Promise<NotificationDeliveryLog> => {
    const response = await apiClient.get(`${API_PATH}/delivery-logs/${id}`);
    return response.data.data;
  },

  retry: async (logId: number): Promise<void> => {
    await apiClient.post(`${API_PATH}/retry/${logId}`);
  }
};

/**
 * Notification Trigger API
 */
export const notificationTriggerApi = {
  trigger: async (data: TriggerNotificationRequest): Promise<void> => {
    await apiClient.post(`${API_PATH}/trigger`, data);
  }
};

/**
 * Program-specific notification configuration API
 */
export const programNotificationConfigurationApi = {
  getProgramConfiguration: async (programId: string): Promise<{
    programId: string;
    programName: string;
    channels: NotificationChannel[];
    templates: NotificationTemplate[];
    rules: NotificationRule[];
  }> => {
    const response = await apiClient.get<ApiResponse<{
      programId: string;
      programName: string;
      channels: NotificationChannel[];
      templates: NotificationTemplate[];
      rules: NotificationRule[];
    }>>(`/programs/notification-configuration/${programId}`);
    return response.data.data;
  },

  saveProgramConfiguration: async (
    programId: string,
    payload: SaveProgramNotificationConfigurationRequest
  ): Promise<{ programId: string; updatedRules: number }> => {
    const response = await apiClient.post<ApiResponse<{
      programId: string;
      updatedRules: number;
    }>>(`/programs/${programId}/notifications`, payload);
    return response.data.data;
  }
};

export const notificationDynamicFieldApi = {
  getAll: async (programId: string) => {
    const response = await apiClient.get(`/programs/${programId}/notifications/dynamic-fields`);
    return response.data.data;
  },

  getById: async (programId: string, fieldId: string) => {
    const response = await apiClient.get(`/programs/${programId}/notifications/dynamic-fields/${fieldId}`);
    return response.data.data;
  },

  create: async (programId: string, payload: unknown) => {
    const response = await apiClient.post(`/programs/${programId}/notifications/dynamic-fields`, payload);
    return response.data.data;
  },

  update: async (programId: string, fieldId: string, payload: unknown) => {
    const response = await apiClient.put(`/programs/${programId}/notifications/dynamic-fields/${fieldId}`, payload);
    return response.data.data;
  },

  archive: async (programId: string, fieldId: string) => {
    const response = await apiClient.post(`/programs/${programId}/notifications/dynamic-fields/${fieldId}/archive`);
    return response.data.data;
  },

  getMapping: async (programId: string, fieldId: string) => {
    const response = await apiClient.get(`/programs/${programId}/notifications/dynamic-fields/${fieldId}/mapping`);
    return response.data.data;
  },

  updateMapping: async (programId: string, fieldId: string, payload: unknown) => {
    const response = await apiClient.put(`/programs/${programId}/notifications/dynamic-fields/${fieldId}/mapping`, payload);
    return response.data.data;
  },

  getAvailableFormFields: async (programId: string, params?: { formId?: string; formVersionId?: string; entityType?: string }) => {
    const response = await apiClient.get(`/programs/${programId}/notifications/dynamic-fields/available-form-fields`, { params });
    return response.data.data;
  },

  getVariables: async (programId: string) => {
    const response = await apiClient.get(`/programs/${programId}/notifications/dynamic-fields/variables`);
    return response.data.data;
  },

  preview: async (programId: string, payload: unknown) => {
    const response = await apiClient.post(`/programs/${programId}/notifications/dynamic-fields/preview`, payload);
    return response.data.data;
  },
};

/**
 * Consolidated notification configuration service
 */
export const notificationConfigurationService = {
  channels: notificationChannelApi,
  templates: notificationTemplateApi,
  retryPolicies: notificationRetryPolicyApi,
  rules: notificationRuleApi,
  recipients: notificationRuleRecipientApi,
  deliveryLogs: notificationDeliveryLogApi,
  dynamicFields: notificationDynamicFieldApi,
  trigger: notificationTriggerApi,
  program: programNotificationConfigurationApi
};